import { useEffect, useState } from 'react';

/**
 * Default fallback colors when canvas analysis cannot be performed
 * (CORS-tainted image, image load error). Stay on safe dark theme that
 * keeps text contrast readable. Issue #884.
 */
const DEFAULT_FADE_COLOR = 'rgb(15, 15, 18)';
const DEFAULT_MID_COLOR = 'rgb(40, 40, 48)';

/** Canvas dimensions for sampling (small to keep getImageData() fast). */
const SAMPLE_WIDTH = 32;
const SAMPLE_HEIGHT = 32;

/**
 * Bottom 35% of the photo is where the glass card overlays — that
 * region's average luminance decides the dark/light theme of the card.
 */
const BOTTOM_REGION_RATIO = 0.65;

/** Luminance below this threshold = "dark" bottom region → use dark theme. */
const DARK_LUMINANCE_THRESHOLD = 128;

/**
 * Slice of the luminance-sorted pixels used as `fadeColor`
 * (the densest bottom stop of the gradient overlay).
 */
const DARKEST_QUANTILE = 0.1;

/** Mid-range slice (33%-66% by luminance) — carries the photo hue. */
const MID_QUANTILE_START = 0.33;
const MID_QUANTILE_END = 0.66;

/**
 * BT.601 luminance coefficients — perceptual brightness weight
 * (R=0.299, G=0.587, B=0.114).
 */
const LUMA_R = 0.299;
const LUMA_G = 0.587;
const LUMA_B = 0.114;

/**
 * Analyzes the hero photo via a hidden canvas and returns:
 *  - bgIsDark   — true if the bottom 35% is dark on average
 *  - fadeColor  — avg color of the darkest 10% of pixels (gradient bottom stop)
 *  - midColor   — avg color of mid-luminance pixels (carries photo hue,
 *                 used as middle gradient stop)
 *
 * Falls back to safe dark defaults if the canvas read fails (CORS, etc.).
 *
 * @param {string|null} imageUrl — fully-resolved image URL (e.g. imgproxy
 *   output). Hook does NOT trigger on raw paths to avoid double work.
 * @param {boolean} [enabled=true] — pass `false` to disable analysis (e.g.
 *   when the page does not need adaptive contrast). When disabled, returns
 *   `bgIsDark = false` for compatibility with the light theme branch.
 */
export const useHeroPhotoAnalysis = (imageUrl, enabled = true) => {
  const [bgIsDark, setBgIsDark] = useState(true);
  const [fadeColor, setFadeColor] = useState(DEFAULT_FADE_COLOR);
  const [midColor, setMidColor] = useState(DEFAULT_MID_COLOR);

  useEffect(() => {
    if (!enabled) {
      setBgIsDark(false);
      return undefined;
    }
    if (!imageUrl) {
      return undefined;
    }

    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    let cancelled = false;

    img.onload = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_WIDTH;
        canvas.height = SAMPLE_HEIGHT;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT);
        const data = ctx.getImageData(0, 0, SAMPLE_WIDTH, SAMPLE_HEIGHT).data;

        const bottomStartRow = Math.floor(SAMPLE_HEIGHT * BOTTOM_REGION_RATIO);
        let totalBottom = 0;
        let countBottom = 0;
        const allPixels = [];

        for (let y = 0; y < SAMPLE_HEIGHT; y += 1) {
          for (let x = 0; x < SAMPLE_WIDTH; x += 1) {
            const i = (y * SAMPLE_WIDTH + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const lum = LUMA_R * r + LUMA_G * g + LUMA_B * b;
            allPixels.push({ r, g, b, lum });
            if (y >= bottomStartRow) {
              totalBottom += lum;
              countBottom += 1;
            }
          }
        }

        const avgBottom = countBottom ? totalBottom / countBottom : 255;
        setBgIsDark(avgBottom < DARK_LUMINANCE_THRESHOLD);

        if (allPixels.length) {
          allPixels.sort((a, b) => a.lum - b.lum);
          const total = allPixels.length;
          const avgOf = (start, end) => {
            const slice = allPixels.slice(start, end);
            let r = 0;
            let g = 0;
            let b = 0;
            slice.forEach((p) => { r += p.r; g += p.g; b += p.b; });
            const n = slice.length || 1;
            return `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
          };
          setFadeColor(avgOf(0, Math.max(1, Math.floor(total * DARKEST_QUANTILE))));
          setMidColor(avgOf(Math.floor(total * MID_QUANTILE_START), Math.floor(total * MID_QUANTILE_END)));
        }
      } catch (e) {
        // CORS-tainted canvas or other read failure — keep defaults.
        console.debug('[useHeroPhotoAnalysis] cannot analyze hero photo:', e?.message);
      }
    };

    img.onerror = () => { /* keep defaults */ };
    img.src = imageUrl;

    return () => {
      // Detach handlers + clear src so the browser aborts the in-flight
      // load. Without `img.src = ''` the request keeps streaming bytes
      // even after the component unmounts (см. discussion на MR #787).
      cancelled = true;
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [imageUrl, enabled]);

  return { bgIsDark, fadeColor, midColor };
};

export default useHeroPhotoAnalysis;
